/**
 * Multi-select picker for a Solution's Constraints — the "+ Add" on each group row of
 * the Solution Constraints card.
 *
 * One list, every group, sticky headings, a search that matches on name and group: a
 * reader who knows "Salesforce" but not which group it lives in still finds it, and the
 * first-time job (zero Constraints → a scored Solution) spans groups in a single visit.
 * The popover stays open while rows are ticked and unticked — each toggle fires its own
 * mutation at once — and closes on Done, Escape, or a click outside.
 *
 * Presentational: the caller owns the library query, the selected/pending sets and the
 * mutations, so the same picker can serve the Account Constraint Profile card later.
 *
 * Rows are `role="option"` buttons with a drawn check square rather than the Radix
 * Checkbox: a checkbox is itself a button, and a button inside a button is invalid HTML
 * and a double tab stop. Keyboard follows SearchableSelect — the search input owns
 * focus, ↑/↓ move an active row tracked by value (so filtering never strands the
 * highlight), Enter toggles it, and Space stays a space in the search box.
 */

import { Building2, Check, Loader2, Plus, Search } from "lucide-react";
import { type KeyboardEvent as ReactKeyboardEvent, useId, useMemo, useRef, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import type { ConstraintPickerOption } from "@/lib/constraint-groups";
import { groupOptions, matchesQuery } from "@/lib/option-search";
import { cn } from "@/lib/utils";

interface Props {
  /** Every active Constraint in the library, canonical-group-first (`toPickerOptions`). */
  options: readonly ConstraintPickerOption[];
  /** The group whose heading the list opens scrolled to — the row this trigger sits on. */
  initialGroup: string;
  /** Constraints already on the Solution. */
  selectedIds: ReadonlySet<string>;
  /** The subset of `selectedIds` that came from the Account. */
  inheritedIds: ReadonlySet<string>;
  /** Rows with a mutation in flight — shown busy, not toggleable. */
  pendingIds: ReadonlySet<string>;
  onToggle: (option: ConstraintPickerOption, next: boolean) => void;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** The trigger's accessible name, e.g. "Add Platform constraint". */
  triggerLabel: string;
}

export function ConstraintPickerPopover({
  options,
  initialGroup,
  selectedIds,
  inheritedIds,
  pendingIds,
  onToggle,
  isLoading,
  isError,
  onRetry,
  triggerLabel,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeValue, setActiveValue] = useState("");
  const listId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // `options` arrive canonical-group-first and filtering keeps that order, so the
  // grouped view (consecutive runs) reads Domain → Platform → Offering → ICP.
  const visible = useMemo(
    () => options.filter((option) => matchesQuery(option, query)),
    [options, query],
  );
  const grouped = useMemo(() => groupOptions(visible), [visible]);
  const indexByValue = useMemo(
    () => new Map(visible.map((option, index) => [option.value, index])),
    [visible],
  );
  const selectedCount = useMemo(
    () => options.filter((option) => selectedIds.has(option.value)).length,
    [options, selectedIds],
  );

  // Falls back to the first row whenever the active option has been filtered away.
  const foundIndex = visible.findIndex((option) => option.value === activeValue);
  const activeIndex = foundIndex === -1 ? 0 : foundIndex;

  // `useId` values are not valid CSS selectors, hence getElementById throughout.
  const rowId = (index: number) => `${listId}-${index}`;
  const headingId = (group: string) => `${listId}-group-${encodeURIComponent(group)}`;

  function revealRow(index: number) {
    listRef.current?.ownerDocument.getElementById(rowId(index))?.scrollIntoView({
      block: "nearest",
    });
  }

  /**
   * Scrolls the list itself rather than calling scrollIntoView on the heading: that
   * would also align every scrollable ancestor, and the popover lives in a portal.
   */
  function revealGroup(group: string) {
    const list = listRef.current;
    const heading = list?.ownerDocument.getElementById(headingId(group));
    if (list && heading) list.scrollTop = heading.offsetTop;
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (next) {
      setQuery("");
      // Start on the first row of the trigger's own group, so Enter adds something sensible.
      setActiveValue(options.find((option) => option.group === initialGroup)?.value ?? "");
    }
  }

  function toggle(option: ConstraintPickerOption | undefined) {
    if (option === undefined || pendingIds.has(option.value)) return;
    onToggle(option, !selectedIds.has(option.value));
  }

  function move(step: number) {
    if (visible.length === 0) return;
    const next = (activeIndex + step + visible.length) % visible.length;
    setActiveValue(visible[next]?.value ?? "");
    revealRow(next);
  }

  function onKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      move(1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      move(-1);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      toggle(visible[activeIndex]);
    }
  }

  const ready = !isLoading && !isError && options.length > 0;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="xs"
          aria-label={triggerLabel}
          aria-expanded={open}
          className="border-dashed text-primary hover:text-primary"
        >
          <Plus /> Add
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-80 gap-0 p-0"
        onOpenAutoFocus={(event) => {
          // The search field owns focus — letting Radix move it to the content would
          // eat the first keystroke. The list is mounted by now, so the trigger's own
          // group can be scrolled to as well.
          event.preventDefault();
          inputRef.current?.focus();
          revealGroup(initialGroup);
        }}
      >
        <div className="flex items-center gap-2 border-b border-border px-3">
          <Search className="size-3.5 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search constraints…"
            aria-label="Search constraints"
            aria-controls={listId}
            aria-activedescendant={
              ready && visible[activeIndex] !== undefined ? rowId(activeIndex) : undefined
            }
            className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>

        {isLoading ? (
          <div className="space-y-1.5 p-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : isError ? (
          <ErrorState
            message="Couldn't load the Constraint library."
            onRetry={onRetry}
            className="m-2"
          />
        ) : options.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            No active Constraints in Configuration.
          </p>
        ) : visible.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            No constraints match.
          </p>
        ) : (
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            aria-multiselectable="true"
            aria-label="Constraints"
            className="relative max-h-[min(320px,50vh)] overflow-y-auto p-1"
          >
            {grouped.map((section) => (
              // `role="presentation"` keeps the wrapper out of the accessibility tree
              // (options stay direct children of the listbox) while giving each sticky
              // heading its own containing block, so it scrolls away with its group.
              <div key={section.group ?? "__ungrouped"} role="presentation">
                {section.group !== null && (
                  <div
                    id={headingId(section.group)}
                    aria-hidden="true"
                    className="sticky top-0 z-10 bg-popover px-2 pt-1.5 pb-1 text-xs font-semibold tracking-wider text-muted-foreground uppercase"
                  >
                    {section.group}
                  </div>
                )}
                {section.options.map((option) => {
                  // Flat position, so the arrow keys and this render agree.
                  const index = indexByValue.get(option.value) ?? 0;
                  const selected = selectedIds.has(option.value);
                  const pending = pendingIds.has(option.value);
                  const inherited = inheritedIds.has(option.value);
                  return (
                    <button
                      key={option.value}
                      id={rowId(index)}
                      type="button"
                      role="option"
                      tabIndex={-1}
                      aria-selected={selected}
                      aria-disabled={pending || undefined}
                      aria-label={`${option.label}, ${option.group}${inherited ? ", inherited from the Account" : ""}`}
                      onClick={() => toggle(option)}
                      onMouseEnter={() => setActiveValue(option.value)}
                      className={cn(
                        "flex w-full cursor-default items-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm",
                        index === activeIndex && "bg-accent text-accent-foreground",
                        pending && "pointer-events-none opacity-50",
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          "mt-0.5 grid size-4 shrink-0 place-items-center rounded-sm border border-input transition-colors",
                          selected &&
                            !pending &&
                            "border-primary bg-primary text-primary-foreground",
                        )}
                      >
                        {pending ? (
                          <Loader2 className="size-3 animate-spin text-muted-foreground" />
                        ) : selected ? (
                          <Check className="size-3" />
                        ) : null}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span className="truncate">{option.label}</span>
                          {inherited && (
                            <Building2
                              className="size-3 shrink-0 text-muted-foreground"
                              aria-hidden="true"
                            />
                          )}
                        </span>
                        {option.description && (
                          <span className="block truncate text-xs text-muted-foreground">
                            {option.description}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {ready && (
          <div className="flex items-center justify-between gap-2 border-t border-border py-1 pr-1 pl-3 text-xs text-muted-foreground">
            <span>
              {selectedCount} of {options.length} on this Solution
            </span>
            <Button type="button" variant="ghost" size="xs" onClick={() => handleOpenChange(false)}>
              Done
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
